import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { subscribe } from "@/lib/blog";

/**
 * Inline email capture used by the top banner and the follow block.
 * `tone` = "dark" (on black) or "light" (on white).
 */
export const SubscribeForm = ({ source, tone = "dark", compact = false, testid = "subscribe" }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("loading");
    try {
      const res = await subscribe(email, source);
      setState("done");
      setMessage(
        res.status === "already_subscribed"
          ? "You're already following the journal."
          : "Check your inbox — we've sent a link to confirm."
      );
      setEmail("");
    } catch (err) {
      setState("idle");
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Please check the email address and try again."
      );
    }
  };

  const dark = tone === "dark";
  const inputCls = dark
    ? "bg-transparent text-white placeholder:text-white/40 border-white/30 focus:border-white"
    : "bg-transparent text-[#0A0A0A] placeholder:text-black/40 border-black/30 focus:border-black";
  const btnCls = dark
    ? "bg-white text-[#0A0A0A] hover:bg-white/85"
    : "bg-[#0A0A0A] text-white hover:bg-black/85";

  if (state === "done") {
    return (
      <div
        data-testid={`${testid}-success`}
        className={`text-sm ${dark ? "text-white/80" : "text-black/70"}`}
      >
        {message}
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`${testid}-form`}
      className={`flex items-stretch ${compact ? "gap-2" : "gap-3"} w-full`}
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
        aria-label="Email address"
        data-testid={`${testid}-email-input`}
        className={`flex-1 min-w-0 border-b outline-none transition-colors font-[inherit] ${
          compact ? "text-xs py-1" : "text-base py-2.5"
        } ${inputCls}`}
      />
      <button
        type="submit"
        disabled={state === "loading"}
        data-testid={`${testid}-submit`}
        className={`inline-flex items-center gap-1.5 font-medium transition-colors disabled:opacity-60 ${
          compact ? "px-3 py-1 text-xs" : "px-6 py-3 text-sm"
        } ${btnCls}`}
      >
        {state === "loading" ? "Adding…" : "Follow"} <ArrowUpRight size={compact ? 12 : 16} />
      </button>
    </form>
  );
};

export default SubscribeForm;
